import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { apiSend, ApiError } from "@/lib/api";
import { persistLogin, clearLogin, landingPath, type Role } from "@/lib/auth";
import { updateLogs } from "@/mocks/updateLogs";
import { activityList } from "@/mocks/dashboard";
import useCountUp from "@/hooks/useCountUp";

const brandHero =
  "/assets/img/5586a5a05b430392.jpg";

// Hero 背景轮播图
const heroBackgrounds = [
  {
    url: "/assets/img/039e9b9dd6c1e7eb.jpg",
  },
  {
    url: "/assets/img/13f3457066ecb948.jpg",
  },
  {
    url: "/assets/img/97d826a40e6b30c9.jpg",
  },
  {
    url: "/assets/img/cc6aabf3c70feb69.jpg",
  },
];

const sceneImages = [
  {
    id: "1",
    url: "/assets/img/8a4b57ceaf1ffbed.jpg",
    title: "西陵峡口",
    subtitle: "长江三峡的东大门",
  },
  {
    id: "2",
    url: "/assets/img/e488965e2206d0c8.jpg",
    title: "三峡大坝",
    subtitle: "世界最大水利枢纽",
  },
  {
    id: "3",
    url: "/assets/img/f122a8b2f803af59.jpg",
    title: "瞿塘峡",
    subtitle: "夔门天下雄",
  },
  {
    id: "4",
    url: "/assets/img/1211c8f53653b81a.jpg",
    title: "白帝城",
    subtitle: "刘备托孤之地",
  },
  {
    id: "5",
    url: "/assets/img/1b5f8ee43f1c27a6.jpg",
    title: "巫山神女峰",
    subtitle: "巫山十二峰之最",
  },
  {
    id: "6",
    url: "/assets/img/f58532d8c566005c.jpg",
    title: "三游洞",
    subtitle: "千年溶洞奇观",
  },
];

const features = [
  {
    icon: "ri-speak-line",
    title: "AI 智能讲解",
    desc: "基于游客位置自动触发景点讲解，多音色语音库，让每处风景都有故事。",
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: "ri-user-smile-line",
    title: "趣味换脸",
    desc: "一键与三峡历史人物\u0022换装\u0022合影，生成专属旅游纪念照，分享趣味体验。",
    color: "bg-amber-50 text-amber-600",
  },
  {
    icon: "ri-question-answer-line",
    title: "知识问答",
    desc: "AI 知识库实时回答游客关于三峡历史、地理、文化的问题。",
    color: "bg-rose-50 text-rose-600",
  },
  {
    icon: "ri-map-2-line",
    title: "场景导航",
    desc: "覆盖 28 个核心景点，GPS 精确定位，自动推送景点导览内容。",
    color: "bg-sky-50 text-sky-600",
  },
];

const stats = [
  { value: "424", label: "台设备在线", icon: "ri-glasses-line" },
  { value: "28", label: "个核心景点", icon: "ri-map-pin-line" },
  { value: "2,647", label: "次智能讲解", icon: "ri-mic-line" },
  { value: "1,423", label: "位游客体验", icon: "ri-user-line" },
];

const navLinks = [
  { label: "功能", anchor: "features" },
  { label: "场景", anchor: "scenes" },
  { label: "数据", anchor: "stats" },
  { label: "IP", anchor: "mascot" },
  { label: "动态", anchor: "updates" },
];

// 平滑滚动到锚点
function scrollToAnchor(anchorId: string) {
  const el = document.getElementById(anchorId);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

// 滚动触发动画 hook
function useScrollAnimation(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

// 滚动动画包装组件
function ScrollReveal({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const { ref, isVisible } = useScrollAnimation();
  const delayClass = delay
    ? `scroll-animate-delay-${Math.min(delay, 6)}`
    : "";

  return (
    <div
      ref={ref}
      className={`scroll-animate ${delayClass} ${isVisible ? "is-visible" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

// 版本标签颜色
function getBadgeStyle(type: string) {
  switch (type) {
    case "feature":
      return "bg-emerald-100 text-emerald-700";
    case "fix":
      return "bg-rose-100 text-rose-700";
    case "optimize":
      return "bg-amber-100 text-amber-700";
    case "content":
      return "bg-sky-100 text-sky-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

// 活动类型图标
function getActivityIcon(type: string) {
  switch (type) {
    case "pair":
      return "ri-link";
    case "question":
      return "ri-chat-3-line";
    case "faceswap":
      return "ri-image-line";
    case "report":
      return "ri-file-list-line";
    case "device":
      return "ri-wifi-off-line";
    default:
      return "ri-notification-line";
  }
}

function getActivityColor(type: string) {
  switch (type) {
    case "pair":
      return "bg-emerald-50 text-emerald-600";
    case "question":
      return "bg-sky-50 text-sky-600";
    case "faceswap":
      return "bg-rose-50 text-rose-600";
    case "report":
      return "bg-amber-50 text-amber-600";
    case "device":
      return "bg-orange-50 text-orange-600";
    default:
      return "bg-gray-50 text-gray-600";
  }
}

// Hero 背景轮播 + 视差组件
function HeroBackground() {
  const [currentBg, setCurrentBg] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });

  // 自动轮播 - 间隔从 5 秒延长到 6 秒
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBg((prev) => (prev + 1) % heroBackgrounds.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  // 视差效果
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 12;
      const y = (e.clientY / window.innerHeight - 0.5) * 12;
      setParallax({ x, y });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* 背景图层 */}
      <div
        ref={containerRef}
        className="absolute inset-0 scale-110"
        style={{
          transform: `translate(${parallax.x}px, ${parallax.y}px) scale(1.1)`,
          transition: "transform 0.5s ease-out",
        }}
      >
        {heroBackgrounds.map((bg, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-[1200ms] ${
              idx === currentBg ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={bg.url}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>

      {/* 遮罩层 */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-white"></div>

      {/* 轮播指示器 */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        {heroBackgrounds.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentBg(idx)}
            className={`h-1.5 rounded-full transition-all duration-[600ms] cursor-pointer ${
              idx === currentBg
                ? "w-8 bg-white/80"
                : "w-3 bg-white/30 hover:bg-white/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

// 数据统计卡片（带计数动画）
function StatCard({ value, label, icon }: { value: string; label: string; icon: string }) {
  const { displayValue, ref } = useCountUp(value, 1500, 0.2);

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 text-center hover:shadow-lg hover:shadow-emerald-50/30 transition-all duration-300">
      <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 flex items-center justify-center">
        <i className={`${icon} text-white text-xl`}></i>
      </div>
      <div ref={ref} className="text-3xl font-bold text-gray-900 mb-1 tabular-nums">
        {displayValue}
      </div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [activeScene, setActiveScene] = useState(0);
  const [sceneFading, setSceneFading] = useState(false);
  const autoTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 登录状态管理
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginError, setLoginError] = useState("");
  const [currentUser, setCurrentUser] = useState("");
  const [rememberPass, setRememberPass] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [transitionText, setTransitionText] = useState("");
  // 移动端菜单
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // 初始化登录状态
  useEffect(() => {
    // Only auto-resume a session that has a real auth token (issued by the
    // backend login). Pre-auth mock sessions (flag set, no token) are dropped so
    // everyone must re-authenticate with the real password once.
    const saved = localStorage.getItem("guide_user_logged_in");
    const token = localStorage.getItem("guide_auth_token");
    if (saved === "true" && token) {
      setIsLoggedIn(true);
      const user = localStorage.getItem("guide_username");
      if (user) setCurrentUser(user);
    } else if (saved === "true") {
      localStorage.removeItem("guide_user_logged_in");
      localStorage.removeItem("guide_username");
    }
    // 记住密码回填
    const savedRemember = localStorage.getItem("guide_remember_pass");
    const savedPass = localStorage.getItem("guide_saved_pass");
    const savedUser = localStorage.getItem("guide_saved_user");
    if (savedRemember === "true" && savedPass && savedUser) {
      setRememberPass(true);
      setLoginUser(savedUser);
      setLoginPass(savedPass);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginUser.trim() || !loginPass.trim()) {
      setLoginError("请输入用户名和密码");
      return;
    }
    setLoginError("");
    setLoginLoading(true);

    // 真鉴权：后端先查 users 表（ops 账号），再回退 ADMIN_USERNAME/ADMIN_PASSWORD。
    // 响应里的 role 决定登陆后的着陆页和侧边栏可见菜单。
    try {
      const res = await apiSend<{ ok: boolean; username: string; role: Role; token: string }>(
        "POST", "/auth/login", { username: loginUser.trim(), password: loginPass },
      );

      // 记住密码逻辑
      if (rememberPass) {
        localStorage.setItem("guide_remember_pass", "true");
        localStorage.setItem("guide_saved_pass", loginPass);
        localStorage.setItem("guide_saved_user", loginUser.trim());
      } else {
        localStorage.removeItem("guide_remember_pass");
        localStorage.removeItem("guide_saved_pass");
        localStorage.removeItem("guide_saved_user");
      }
      const role: Role = res.role === "ops" ? "ops" : "admin";

      // 过渡动画阶段
      setTimeout(() => {
        setTransitionText("验证通过");
        setShowTransition(true);
      }, 400);

      setTimeout(() => {
        setTransitionText("加载中...");
      }, 900);

      setTimeout(() => {
        setTransitionText("欢迎回来");
      }, 1400);

      setTimeout(() => {
        setIsLoggedIn(true);
        setCurrentUser(loginUser.trim());
        persistLogin(loginUser.trim(), role, res.token);
        setLoginPass("");
        setLoginLoading(false);
        setShowTransition(false);
        setTransitionText("");
        navigate(landingPath(role));
      }, 2000);
    } catch (err) {
      setLoginLoading(false);
      setLoginError(
        err instanceof ApiError
          ? err.status === 401
            ? "账号或密码错误"
            : err.status === 503
              ? "管理员账号未配置，请联系管理员"
              : "登录失败，请重试"
          : "网络异常，请重试",
      );
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setLoginUser("");
    setLoginPass("");
    setCurrentUser("");
    setRememberPass(false);
    clearLogin();
    localStorage.removeItem("guide_remember_pass");
    localStorage.removeItem("guide_saved_pass");
    localStorage.removeItem("guide_saved_user");
  };

  // 功能卡片数字计数动画
  const { displayValue: countDisplay1, ref: countRef1 } = useCountUp("2,647", 2000, 0.1);
  const { displayValue: countDisplay2, ref: countRef2 } = useCountUp("156", 2000, 0.1);
  const { displayValue: countDisplay3, ref: countRef3 } = useCountUp("1,423", 2000, 0.1);
  const { displayValue: countDisplay4, ref: countRef4 } = useCountUp("28", 2000, 0.1);

  // 场景画廊自动轮播
  useEffect(() => {
    autoTimerRef.current = setInterval(() => {
      setSceneFading(true);
      setTimeout(() => {
        setActiveScene((prev) => (prev + 1) % sceneImages.length);
        setSceneFading(false);
      }, 250);
    }, 5000);
    return () => {
      if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    };
  }, []);

  const handleSceneChange = (idx: number) => {
    if (idx === activeScene) return;
    setSceneFading(true);
    setTimeout(() => {
      setActiveScene(idx);
      setSceneFading(false);
    }, 250);
    // 重置自动轮播
    if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    autoTimerRef.current = setInterval(() => {
      setSceneFading(true);
      setTimeout(() => {
        setActiveScene((prev) => (prev + 1) % sceneImages.length);
        setSceneFading(false);
      }, 250);
    }, 5000);
  };

  return (
    <div className="min-h-screen bg-white relative">
      {/* ============ 顶部导航栏 ============ */}
      <header className="absolute top-0 left-0 right-0 z-30 px-4 md:px-6 lg:px-10 py-5 flex items-center justify-between animate-fade-in bg-gradient-to-b from-black/50 to-transparent">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-400 rounded-xl flex items-center justify-center shadow-lg shadow-black/20 flex-shrink-0">
            <i className="ri-glasses-line text-white text-lg"></i>
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-white leading-tight drop-shadow-md">
              三峡·两坝一峡
            </span>
            <span className="text-xs text-white/80 leading-tight drop-shadow">
              导览运营台
            </span>
          </div>
        </div>
        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <a
              key={link.anchor}
              href={`#${link.anchor}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToAnchor(link.anchor);
              }}
              className="text-sm text-white/90 hover:text-white hover:underline underline-offset-4 transition-colors duration-200 drop-shadow-sm font-medium"
            >
              {link.label}
            </a>
          ))}
          {isLoggedIn ? (
            <div className="flex items-center gap-3 ml-2 pl-4 border-l border-white/30">
              <span className="text-sm text-white/90 drop-shadow-sm">
                <i className="ri-user-line mr-1"></i>
                {currentUser}
              </span>
              <button
                onClick={handleLogout}
                className="text-sm text-white/80 hover:text-white hover:underline underline-offset-4 transition-colors duration-200 cursor-pointer drop-shadow-sm"
              >
                退出
              </button>
            </div>
          ) : null}
        </nav>
        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-white"
        >
          <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </header>

      {/* Mobile menu panel */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="absolute top-0 right-0 w-64 h-full bg-gray-900/95 backdrop-blur-md pt-20 px-6 flex flex-col gap-4 animate-[slideIn_0.2s_ease-out]">
            {navLinks.map((link) => (
              <a
                key={link.anchor}
                href={`#${link.anchor}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToAnchor(link.anchor);
                  setMobileMenuOpen(false);
                }}
                className="text-base text-white/90 hover:text-white font-medium py-2 border-b border-white/10"
              >
                {link.label}
              </a>
            ))}
            {isLoggedIn ? (
              <>
                <div className="text-sm text-white/70 pt-2">
                  <i className="ri-user-line mr-1"></i>
                  {currentUser}
                </div>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="text-sm text-white/60 hover:text-white py-2 text-left"
                >
                  退出登录
                </button>
              </>
            ) : (
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="mt-4 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-400 text-white text-sm font-medium rounded-xl text-center"
              >
                登录
              </button>
            )}
          </div>
        </div>
      )}

      {/* ============ Hero 区域 ============ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* 自动轮播视差背景 */}
        <HeroBackground />

        <div className="relative z-10 w-full max-w-3xl mx-auto px-6 lg:px-10 pt-28 pb-20 flex flex-col items-center text-center gap-6">
          {/* 品牌图 — 去掉白色背景光晕，保持自然透明 */}
          <div className="w-full max-w-md animate-fade-in-up relative">
            <div className="relative z-10 px-4">
              <img
                src={brandHero}
                alt="AI领航镜游三峡"
                className="w-full h-auto"
                style={{
                  filter: "drop-shadow(0 30px 60px rgba(0,0,0,0.15))",
                }}
              />
            </div>
          </div>

          {/* 标题 + 描述 */}
          <div className="relative z-10 space-y-3 max-w-lg animate-fade-in-up delay-200">
            <h1 className="text-3xl lg:text-4xl font-extrabold text-white leading-tight tracking-tight drop-shadow-lg">
              AI 领航
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-teal-200">
                {" "}
                · 镜游三峡
              </span>
            </h1>
            <p className="text-sm text-white/90 leading-relaxed drop-shadow-md mt-2">
              用 AI 智能眼镜，解锁全新三峡之旅。沉浸式讲解、智能问答、趣味换脸，让每一位游客都能深度体验「两坝一峡」的壮丽风光。
            </p>
          </div>

          {/* 信任关键词标签 */}
          <div className="flex items-center flex-wrap justify-center gap-3 animate-fade-in-up delay-300">
            {["两坝一峡", "实时导航", "AI 智能", "28 个景点", "多音色讲解"].map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 backdrop-blur-sm border border-white/20 rounded-full text-xs text-white/95 font-medium"
              >
                <i className="ri-check-line text-emerald-300 text-[10px]"></i>
                {tag}
              </span>
            ))}
          </div>

          {/* 登录 / 进入工作台 */}
          <div className="w-full max-w-sm animate-fade-in-up delay-400">
            {/* 登录过渡动画层 */}
            {loginLoading && (
              <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"></div>
                <div className="relative z-10 flex flex-col items-center gap-4">
                  {/* 旋转光环 */}
                  <div className="relative w-20 h-20">
                    <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-emerald-400 animate-spin"></div>
                    <div className="absolute inset-1.5 rounded-full border-2 border-transparent border-t-teal-300 animate-spin" style={{ animationDuration: "1.5s", animationDirection: "reverse" }}></div>
                    <div className="absolute inset-3 rounded-full border-2 border-transparent border-t-emerald-200 animate-spin" style={{ animationDuration: "2s" }}></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <i className="ri-glasses-line text-white text-2xl"></i>
                    </div>
                  </div>
                  {/* 动态文字 */}
                  <div className="h-8 flex items-center">
                    <span
                      key={transitionText}
                      className="text-white text-lg font-medium animate-fade-in-up"
                    >
                      {transitionText}
                    </span>
                  </div>
                  {/* 进度条 */}
                  <div className="w-48 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-300 rounded-full animate-pulse" style={{ width: "60%" }}></div>
                  </div>
                </div>
              </div>
            )}

            {isLoggedIn ? (
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-400 text-white text-sm font-medium rounded-xl hover:shadow-lg hover:shadow-emerald-200/40 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-dashboard-line"></i>
                进入工作台
              </button>
            ) : (
              <form
                onSubmit={handleLogin}
                className="w-full bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-left"
              >
                <div className="space-y-3">
                  <div className="relative">
                    <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-white/60 text-sm"></i>
                    <input
                      type="text"
                      value={loginUser}
                      onChange={(e) => {
                        setLoginUser(e.target.value);
                        setLoginError("");
                      }}
                      placeholder="用户名"
                      className="w-full pl-9 pr-4 py-2.5 bg-white/15 border border-white/40 rounded-xl text-sm text-white placeholder-white/60 outline-none focus:bg-white/25 focus:border-white/60 focus:ring-1 focus:ring-white/20 transition-all duration-200"
                    />
                  </div>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-white/60 text-sm"></i>
                    <input
                      type="password"
                      value={loginPass}
                      onChange={(e) => {
                        setLoginPass(e.target.value);
                        setLoginError("");
                      }}
                      placeholder="密码"
                      className="w-full pl-9 pr-4 py-2.5 bg-white/15 border border-white/40 rounded-xl text-sm text-white placeholder-white/60 outline-none focus:bg-white/25 focus:border-white/60 focus:ring-1 focus:ring-white/20 transition-all duration-200"
                    />
                  </div>
                </div>

                {/* 记住密码 */}
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setRememberPass(!rememberPass)}
                    className="flex items-center gap-2 cursor-pointer group"
                  >
                    <div
                      className={`w-4 h-4 rounded border transition-all duration-200 flex items-center justify-center ${
                        rememberPass
                          ? "bg-emerald-400 border-emerald-400"
                          : "border-white/50 bg-transparent group-hover:border-white/70"
                      }`}
                    >
                      {rememberPass && (
                        <i className="ri-check-line text-white text-[10px]"></i>
                      )}
                    </div>
                    <span className="text-xs text-white/70 group-hover:text-white/90 transition-colors duration-200">
                      记住密码
                    </span>
                  </button>
                </div>

                {loginError && (
                  <div className="mt-3 text-xs text-rose-400 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {loginError}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loginLoading}
                  className="mt-4 w-full px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-400 text-white text-sm font-medium rounded-xl hover:shadow-lg hover:shadow-emerald-200/30 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-login-box-line"></i>
                  登录
                </button>
                <p className="mt-3 text-center text-xs text-white/50">
                  输入任意用户名和密码即可登录体验
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ============ 核心功能介绍 ============ */}
      <section id="features" className="py-16 px-6 lg:px-10 bg-gray-50/50">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">核心功能</h2>
              <p className="text-gray-500 text-sm">AI 智能眼镜深度能力，持续拓展旅游体验边界</p>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <ScrollReveal>
              <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-emerald-200 hover:shadow-lg hover:shadow-emerald-50/50 transition-all duration-300 group cursor-pointer h-full" onClick={() => navigate("/audio")}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-r from-emerald-500 to-teal-400 text-white">
                  <i className="ri-speak-line text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">AI 智能讲解</h3>
                <p className="text-sm text-gray-500 leading-relaxed">基于游客位置自动触发景点讲解，多音色语音库，让每处风景都有故事。</p>
                <div className="mt-4 flex items-center gap-3">
                  <span ref={countRef1} className="text-2xl font-bold text-gray-900 tabular-nums">{countDisplay1}</span>
                  <span className="text-xs text-gray-500">次讲解已触发</span>
                </div>
                <div className="mt-3 flex items-center gap-1 text-sm text-emerald-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>了解更多</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-amber-200 hover:shadow-lg hover:shadow-amber-50/50 transition-all duration-300 group cursor-pointer h-full" onClick={() => navigate("/faceswap")}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-r from-amber-500 to-orange-400 text-white">
                  <i className="ri-user-smile-line text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">趣味换脸</h3>
                <p className="text-sm text-gray-500 leading-relaxed">一键与三峡历史人物"换装"合影，生成专属旅游纪念照，分享趣味体验。</p>
                <div className="mt-4 flex items-center gap-3">
                  <span ref={countRef2} className="text-2xl font-bold text-gray-900 tabular-nums">{countDisplay2}</span>
                  <span className="text-xs text-gray-500">张纪念照已生成</span>
                </div>
                <div className="mt-3 flex items-center gap-1 text-sm text-amber-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>了解更多</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-rose-200 hover:shadow-lg hover:shadow-rose-50/50 transition-all duration-300 group cursor-pointer h-full" onClick={() => navigate("/knowledge/graph")}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-r from-rose-500 to-pink-400 text-white">
                  <i className="ri-question-answer-line text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">知识问答</h3>
                <p className="text-sm text-gray-500 leading-relaxed">AI 知识库实时回答游客关于三峡历史、地理、文化的问题。</p>
                <div className="mt-4 flex items-center gap-3">
                  <span ref={countRef3} className="text-2xl font-bold text-gray-900 tabular-nums">{countDisplay3}</span>
                  <span className="text-xs text-gray-500">位游客已体验</span>
                </div>
                <div className="mt-3 flex items-center gap-1 text-sm text-rose-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>了解更多</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-sky-200 hover:shadow-lg hover:shadow-sky-50/50 transition-all duration-300 group cursor-pointer h-full" onClick={() => navigate("/scenes")}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-r from-sky-500 to-cyan-400 text-white">
                  <i className="ri-map-2-line text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">场景导航</h3>
                <p className="text-sm text-gray-500 leading-relaxed">覆盖 28 个核心景点，GPS 精确定位，自动推送景点导览内容。</p>
                <div className="mt-4 flex items-center gap-3">
                  <span ref={countRef4} className="text-2xl font-bold text-gray-900 tabular-nums">{countDisplay4}</span>
                  <span className="text-xs text-gray-500">个核心景点覆盖</span>
                </div>
                <div className="mt-3 flex items-center gap-1 text-sm text-sky-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>了解更多</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ============ 场景画廊 ============ */}
      <section id="scenes" className="py-20 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                三峡风光
              </h2>
              <p className="text-gray-500 text-base">
                28 个核心景点，覆盖两坝一峡全程
              </p>
            </div>
          </ScrollReveal>

          {/* 大图展示 */}
          <ScrollReveal>
            <div className="relative rounded-2xl overflow-hidden mb-6 group">
              <div className="w-full h-[420px] md:h-[520px] bg-gray-900">
                <img
                  src={sceneImages[activeScene].url}
                  alt={sceneImages[activeScene].title}
                  className={`w-full h-full object-cover transition-opacity duration-500 group-hover:scale-105 ${
                    sceneFading ? "opacity-0" : "opacity-100"
                  }`}
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-1">
                  {sceneImages[activeScene].title}
                </h3>
                <p className="text-white/80 text-base">
                  {sceneImages[activeScene].subtitle}
                </p>
              </div>
            </div>
          </ScrollReveal>

          {/* 缩略图切换 - 6个场景 */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {sceneImages.map((scene, idx) => (
              <button
                key={scene.id}
                onClick={() => handleSceneChange(idx)}
                className={`relative rounded-xl overflow-hidden transition-all duration-300 cursor-pointer ${
                  activeScene === idx
                    ? "ring-2 ring-emerald-500 ring-offset-2"
                    : "opacity-70 hover:opacity-100"
                }`}
              >
                <div className="w-full h-20 md:h-24">
                  <img
                    src={scene.url}
                    alt={scene.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute bottom-1.5 left-1.5 right-1.5">
                  <span className="text-xs font-medium text-white truncate block">
                    {scene.title}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 数据亮点 ============ */}
      <section id="stats" className="py-20 px-6 lg:px-10 bg-gradient-to-r from-emerald-50/60 to-teal-50/40">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                运营数据
              </h2>
              <p className="text-gray-500 text-base">
                实时数据看板，了解导览系统运行状态
              </p>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {stats.map((s, idx) => (
              <ScrollReveal key={s.label} delay={idx + 1}>
                <StatCard value={s.value} label={s.label} icon={s.icon} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 语音角色 ============ */}
      <section id="voices" className="py-20 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                语音角色
              </h2>
              <p className="text-gray-500 text-base">
                多角色配音，为不同场景匹配最合适的讲解声音
              </p>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <ScrollReveal delay={1}>
              <div className="bg-white rounded-2xl p-6 border border-gray-100 flex items-center gap-5 hover:shadow-lg hover:shadow-emerald-50/30 transition-all duration-300">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 flex items-center justify-center flex-shrink-0">
                  <i className="ri-sailboat-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    鲟侠船长
                  </h3>
                  <p className="text-sm text-gray-500">
                    沉稳厚重的男声，适合历史典故、工程科普类讲解
                  </p>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={2}>
              <div className="bg-white rounded-2xl p-6 border border-gray-100 flex items-center gap-5 hover:shadow-lg hover:shadow-amber-50/30 transition-all duration-300">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 flex items-center justify-center flex-shrink-0">
                  <i className="ri-women-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    金梅导游
                  </h3>
                  <p className="text-sm text-gray-500">
                    亲切温暖的女声，适合自然风光、人文故事类讲解
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ============ 品牌吉祥物IP展示区 ============ */}
      <section id="mascot" className="py-20 px-6 lg:px-10 bg-gradient-to-b from-amber-50/30 to-emerald-50/20">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                品牌吉祥物
              </h2>
              <p className="text-gray-500 text-base">
                陪伴游客探索两坝一峡的 IP 伙伴
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <ScrollReveal delay={1}>
              <div className="group bg-white rounded-2xl border border-gray-100 p-6 text-center hover:shadow-lg hover:shadow-emerald-50/30 transition-all duration-300">
                <div className="w-32 h-32 mx-auto mb-5 rounded-full bg-gradient-to-r from-emerald-50 to-teal-50 flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-1">
                  <img
                    src="/assets/img/dcab9a3a97a2856b.jpg"
                    alt="鲟侠船长"
                    className="w-28 h-28 object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  鲟侠船长
                </h3>
                <div className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full mb-4">
                  中华鲟船长
                </div>
                <p className="text-sm text-gray-500 leading-relaxed">
                  以国家一级保护动物「中华鲟」为原型设计，象征着三峡生态与长江文明的守护者。沉稳可靠的船长形象，为游客讲述大坝工程的壮观与历史典故。
                </p>
                <div className="mt-4 flex items-center justify-center gap-3 text-sm">
                  <span className="flex items-center gap-1 text-emerald-600">
                    <i className="ri-speak-line"></i>
                    语音讲解
                  </span>
                  <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                  <span className="flex items-center gap-1 text-emerald-600">
                    <i className="ri-question-answer-line"></i>
                    知识问答
                  </span>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={2}>
              <div className="group bg-white rounded-2xl border border-gray-100 p-6 text-center hover:shadow-lg hover:shadow-amber-50/30 transition-all duration-300">
                <div className="w-32 h-32 mx-auto mb-5 rounded-full bg-gradient-to-r from-amber-50 to-orange-50 flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-1">
                  <img
                    src="/assets/img/84d17fc2caa1d962.jpg"
                    alt="金梅导游"
                    className="w-28 h-28 object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  金梅导游
                </h3>
                <div className="inline-block px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full mb-4">
                  金牌导游
                </div>
                <p className="text-sm text-gray-500 leading-relaxed">
                  以三峡地区「腊梅」为灵感设计，象征着坚韧与温暖的人文精神。亲切活泼的导游形象，为游客讲述自然风光的秀丽与人文故事的动人。
                </p>
                <div className="mt-4 flex items-center justify-center gap-3 text-sm">
                  <span className="flex items-center gap-1 text-amber-600">
                    <i className="ri-speak-line"></i>
                    语音讲解
                  </span>
                  <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                  <span className="flex items-center gap-1 text-amber-600">
                    <i className="ri-user-smile-line"></i>
                    趣味换脸
                  </span>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ============ 系统动态 / 版本更新 ============ */}
      <section id="updates" className="py-20 px-6 lg:px-10 bg-gray-50/50">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                系统动态
              </h2>
              <p className="text-gray-500 text-base">
                最新版本更新与实时运营动态
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 左侧：版本更新时间线 */}
            <ScrollReveal delay={1} className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-gray-100 p-6 h-full">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-400 flex items-center justify-center">
                    <i className="ri-time-line text-white text-sm"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    版本更新
                  </h3>
                </div>
                <div className="space-y-5">
                  {updateLogs.slice(0, 4).map((log) => (
                    <div
                      key={log.id}
                      className="relative pl-6 pb-5 border-l border-gray-200 last:pb-0 last:border-0"
                    >
                      <div className="absolute left-0 top-0 w-3 h-3 -translate-x-1.5 rounded-full bg-emerald-400 border-2 border-white shadow-sm"></div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-gray-900">
                          {log.version}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${getBadgeStyle(
                            log.type
                          )}`}
                        >
                          {log.badge}
                        </span>
                        <span className="text-xs text-gray-400 ml-auto">
                          {log.date}
                        </span>
                      </div>
                      <h4 className="text-sm font-medium text-gray-800 mb-1">
                        {log.title}
                      </h4>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        {log.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            {/* 中间：实时运营通知 */}
            <ScrollReveal delay={2} className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-gray-100 p-6 h-full">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-rose-400 to-pink-400 flex items-center justify-center">
                    <i className="ri-notification-3-line text-white text-sm"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    实时运营
                  </h3>
                  <span className="ml-auto flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-xs text-emerald-600">实时</span>
                  </span>
                </div>
                <div className="space-y-4">
                  {activityList.slice(0, 6).map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getActivityColor(
                          item.type
                        )}`}
                      >
                        <i className={`${getActivityIcon(item.type)} text-sm`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 leading-relaxed truncate">
                          {item.content}
                        </p>
                        <span className="text-xs text-gray-400 mt-1">
                          {item.timeLabel}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate("/dashboard")}
                  className="mt-4 w-full py-2.5 text-sm text-emerald-600 font-medium border border-emerald-200 rounded-xl hover:bg-emerald-50 transition-colors duration-200 flex items-center justify-center gap-1"
                >
                  <span>查看全部动态</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </ScrollReveal>

            {/* 右侧：快捷操作 + 今日概览 */}
            <ScrollReveal delay={3} className="lg:col-span-1">
              <div className="space-y-6 h-full flex flex-col">
                {/* 快捷操作 */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6 flex-1">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-amber-400 to-orange-400 flex items-center justify-center">
                      <i className="ri-rocket-line text-white text-sm"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      快捷操作
                    </h3>
                  </div>
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate("/devices")}
                      className="w-full text-left p-3 rounded-xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all duration-200 flex items-center gap-3 cursor-pointer"
                    >
                      <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                        <i className="ri-link text-teal-600 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          批量预配对
                        </div>
                        <div className="text-xs text-gray-500">
                          为下一船次提前生成配对码
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => navigate("/scenes")}
                      className="w-full text-left p-3 rounded-xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all duration-200 flex items-center gap-3 cursor-pointer"
                    >
                      <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
                        <i className="ri-map-pin-add-line text-amber-600 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          新增景点
                        </div>
                        <div className="text-xs text-gray-500">
                          添加新讲解触发点
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => navigate("/faceswap")}
                      className="w-full text-left p-3 rounded-xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all duration-200 flex items-center gap-3 cursor-pointer"
                    >
                      <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center">
                        <i className="ri-image-add-line text-rose-600 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          上传换脸模板
                        </div>
                        <div className="text-xs text-gray-500">
                          上传新主题换脸模板
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => navigate("/analytics")}
                      className="w-full text-left p-3 rounded-xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all duration-200 flex items-center gap-3 cursor-pointer"
                    >
                      <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                        <i className="ri-file-download-line text-emerald-600 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          导出今日报表
                        </div>
                        <div className="text-xs text-gray-500">
                          生成运营数据 Excel
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* 今日概览 */}
                <div className="bg-gradient-to-r from-emerald-500 to-teal-400 rounded-2xl p-6 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                      <i className="ri-bar-chart-line text-white text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-white/90">
                        今日运行概览
                      </div>
                      <div className="text-xs text-white/70">2026-06-09</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">384</div>
                      <div className="text-xs text-white/70">今日发放</div>
                    </div>
                    <div className="text-center border-x border-white/20">
                      <div className="text-2xl font-bold">2,647</div>
                      <div className="text-xs text-white/70">AI 问答</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">156</div>
                      <div className="text-xs text-white/70">换脸生成</div>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate("/dashboard")}
                    className="mt-5 w-full py-2.5 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <i className="ri-dashboard-line"></i>
                    查看详细数据
                  </button>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ============ 底部 CTA ============ */}
      <section id="cta" className="py-16 px-6 lg:px-10 bg-gray-900">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            开始管理您的导览系统
          </h2>
          <p className="text-gray-400 text-base mb-8">
            进入工作台，实时掌握设备状态、场景内容、游客数据
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-8 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-400 text-white text-base font-medium rounded-xl hover:shadow-lg hover:shadow-emerald-500/30 transition-all duration-300 flex items-center gap-2 mx-auto whitespace-nowrap"
          >
            <i className="ri-dashboard-line"></i>
            进入工作台
          </button>
        </div>
      </section>

      {/* ============ 底部版权 ============ */}
      <footer className="py-8 px-6 lg:px-10 bg-gray-950 border-t border-gray-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-400 rounded-lg flex items-center justify-center">
              <i className="ri-glasses-line text-white text-sm"></i>
            </div>
            <span className="text-sm text-gray-400">
              三峡·两坝一峡 AI 智能导览系统
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <a
              href="/dashboard"
              onClick={(e) => {
                e.preventDefault();
                navigate("/dashboard");
              }}
              className="hover:text-gray-300 transition-colors"
            >
              工作台
            </a>
            <a
              href="/devices"
              onClick={(e) => {
                e.preventDefault();
                navigate("/devices");
              }}
              className="hover:text-gray-300 transition-colors"
            >
              设备管理
            </a>
            <a
              href="/scenes"
              onClick={(e) => {
                e.preventDefault();
                navigate("/scenes");
              }}
              className="hover:text-gray-300 transition-colors"
            >
              场景管理
            </a>
          </div>
          <p className="text-xs text-gray-600">© 2026 两坝一峡导览运营台</p>
        </div>
      </footer>
    </div>
  );
}